import React from 'react'
import H1 from '../components/legal/H1'
import H6 from '../components/legal/H6'
import H5 from '../components/legal/H5'
import Medium from '../components/legal/Medium'
import P from '../components/legal/Paragraph'
import { css } from 'glamor'
import { colours } from '../constants/colourConstants'
import LegalHeader from '../components/legal/LegalHeader'
import { Link } from "gatsby"
import IndexSeo from "../images/LandingPage/Index-SEO.png"
import Seo from '../components/global/Seo'


const privacyPolicySections = [
	{
		id: 'WhoFor',
		title: 'Who is this privacy policy for?',
		content: (
			<div>
				<P>This privacy policy is for individuals over whose data we exercise overall control as to the purposes and means of its processing - i.e. those individuals with whom we have or are likely to have a direct relationship.</P>
				<P>Where another organisation is putting your information into the Plexi App (for example, your vet’s practice or your employer if you are employed by a vet’s practice), you should consult that organisation (and their privacy policy) to understand how your data is being used by that organisation. In this situation we are simply processing your personal data in accordance with the instructions provided by the relevant organisation and a contract that we have with them, and they are ultimately responsible for how your data is used.</P>
			</div>
		)
	},
	{
		id: 'WhoWeAre',
		title: 'Important information and who we are',
		content: (
			<div>
				<H6>Purpose of this privacy policy</H6>

				<P>This privacy policy applies to personal information submitted to us on our website, via the Plexi App or when you contact us (for example by phone or email). It also covers any personal information that may be automatically retrieved through your use of our website or the Plexi App. If you do not agree to all the terms of this privacy policy you must not use our website, the Plexi App or contact us to enquire about our services.</P>
				<P>It is important that you read this privacy policy together with any other privacy policy or fair processing policy we may provide on specific occasions when we are collecting or processing personal data about you so that you are fully aware of how and why we are using your data. This privacy policy supplements other notices and privacy policies and is not intended to override them.</P>

				<H6>Controller</H6>

				<P>Plexi Software Limited is the controller and responsible for your personal data. We are a company registered in England and Wales with company registration number 11312901. Our registered office address is at 15 Colmore Row, Birmingham, West Midlands, England, B3 2BH.  The exception to this is where one of our clients is processing your data using our App, in this situation our client will be the controller and is responsible for informing you how your data is being used.</P>
				<P>We have appointed a data protection lead who is responsible for overseeing questions in relation to this privacy policy. If you have any questions about this privacy policy or our privacy practices, including any requests to exercise <a href="#LegalRights">your legal rights</a>, please contact our data protection lead using the details set out below.</P>

				<H6>Contact details</H6>
				<P>If you have any questions about this privacy policy or our privacy practices, please contact our data protection lead in the following ways:</P>
				<div /* abstract */
					{...css({
						padding: "20px 20px 4px 20px",
						backgroundColor: colours.greys.lightest,
						borderRadius: "8px",
						marginBottom: "20px"
					})}
				>
					<P><span {...css({ color: colours.greys.lightBlue })}>Name</span><br />Peter Renwick (Data Protection Lead at Plexi)</P>
					<P><span {...css({ color: colours.greys.lightBlue })}>Email address</span><br /><a href="mailto:peter.renwick@plexi.vet">peter.renwick@plexi.vet</a></P>
					<P><span {...css({ color: colours.greys.lightBlue })}>Alternative email address</span><br /><a href="mailto:info@plexi.vet">info@plexi.vet</a><br /><span {...css({ color: colours.greys.lightBlue, fontSize: "12px" })}>(If possible please mention that your enquiry is to be directed to our data protection lead.)</span></P>
					<P><span {...css({ color: colours.greys.lightBlue })}>Online Contact Form</span><br />Available by clicking "Contact Us" on <Link to="/">our website.</Link><br /><span {...css({ color: colours.greys.lightBlue, fontSize: "12px" })}>(If possible please mention that your enquiry is to be directed to our data protection lead.)</span></P>
				</div>
				<P>You have the right to make a complaint at any time to the Information Commissioner's Office (ICO), the United Kingdom supervisory authority for data protection issues <a href="https://www.ico.org.uk" target="_blank" rel="noopener noreferrer">(www.ico.org.uk)</a>. We would, however, appreciate the chance to deal with your concerns before you approach the ICO so please contact us in the first instance.</P>
				<H6>Changes to the privacy policy and your duty to inform us of changes</H6>

				<P>We keep our privacy policy under regular review. This version was last updated in January 2021.</P>
				<P>It is important that the personal data we hold about you is accurate and current. Please keep us informed if your personal data changes during your relationship with us.</P>

				<H6>Third-party links via the Plexi website</H6>

				<P>Our website may include links to third-party websites, plug-ins and applications. Clicking on those links or enabling those connections may allow third parties to collect or share data about you. We do not control these third-party websites and are not responsible for their privacy statements. When you leave our website or the Plexi App, we encourage you to read the privacy policy of every website you visit.</P>
			</div>
		)
	},
	{
		id: 'DataAboutYou',
		title: 'The data we collect about you',
		content: (
			<div>
				<P>Personal data, or personal information, means any information about an individual from which that person can be identified. It does not include data where the identity has been removed (anonymous data).</P>
				<P>We may collect, use, store and transfer different kinds of personal data about you which we have grouped together as follows:</P>
				<ul>
					<li><P><Medium>Identity Data</Medium> may include: first name, last name, title.</P></li>
					<li><P><Medium>Contact Data</Medium> may include: address, email address and telephone numbers.</P></li>
					<li><P><Medium>Technical Data</Medium> includes internet protocol (IP) address, your login data, browser type and version, time zone setting and location, browser plug-in types and versions, operating system and platform, and other technology on the devices you use to access our website and the Plexi App.</P></li>
					<li><P><Medium>Profile Data</Medium> includes your name, purchases or orders made by you, your interests, preferences, feedback and survey responses.</P></li>
					<li><P><Medium>Usage Data</Medium> includes information about how you use our website, the Plexi App, and our other products and services.</P></li>
					<li><P><Medium>Marketing and Communications Data</Medium> includes your preferences in receiving marketing from us and our third parties and your communication preferences.</P></li>
				</ul>
				<P>We also collect, use and share <Medium>Aggregated Data</Medium> such as statistical or demographic data for any purpose. Aggregated Data could be derived from your personal data but is not considered personal data in law as this data will not directly or indirectly reveal your identity. For example, we may aggregate your Usage Data to calculate the percentage of users accessing a specific Plexi App or website feature. However, if we combine or connect Aggregated Data with your personal data so that it can directly or indirectly identify you, we treat the combined data as personal data which will be used in accordance with this privacy policy.</P>
				<P>We do not collect any <Medium>Special Categories of Personal Data</Medium> about you (this includes details about your race or ethnicity, religious or philosophical beliefs, sex life, sexual orientation, political opinions, trade union membership, information about your health, and genetic and biometric data). Nor do we collect any information about criminal convictions and offences.</P>

				<H6>If you fail to provide personal data</H6>

				<P>Where we need to collect personal data by law, under the terms of a contract we have with you or to provide you with services and you fail to provide that data when requested, we may not be able to perform the contract we have or are trying to enter into with you or certain services may not be available to you.</P>
			</div>
		)
	},
	{
		id: 'DataCollection',
		title: 'How is your personal data collected?',
		content: (
			<div>
				<P>We use different methods to collect data from and about you including through:</P>
				<ul>
					<li>
						<P><Medium>Direct interactions.</Medium> In order to use our website and/or the Plexi App, or if you wish to contact us, you may need to submit certain personal information such as your name, email address, practice name and telephone number. You may be asked to submit further information from time to time. Failure to provide necessary personal information when requested may result in certain services not being available to you.</P>
					</li>
					<li><P><Medium>Automated technologies or interactions.</Medium> If you interact with our website or the Plexi App, we will automatically collect Technical Data about your equipment, browsing actions and patterns. We collect this personal data by using cookies, server logs and other similar technologies. Please see our <Link to="/cookies/">Cookie Policy</Link> for further details.</P></li>
					<li>
						<P><Medium>Third parties or publicly available sources.</Medium> We may receive personal data about you from various third parties as set out below:</P>
						<P><u>Analytics providers</u></P>
						<P><Medium>Google Analytics</Medium></P>
						<P>Google Analytics is a web analytics service offered by Google that tracks and reports website traffic. Google uses the data collected to track and monitor the use of our Service. This data is shared with other Google services. Google may use the collected data to contextualise and personalise the ads of its own advertising network.</P>
						<P>You can opt-out of having made your activity on the Service available to Google Analytics by installing the Google Analytics opt-out browser add-on. The add-on prevents the Google Analytics JavaScript (ga.js, analytics.js and dc.js) from sharing information with Google Analytics about visits activity.</P>
						<P>For more information on the privacy practices of Google, please visit the <a href="https://policies.google.com/privacy?hl=en" target="_blank" rel="noopener noreferrer">Google Privacy & Terms web page.</a></P>
						<P><Medium>Search information providers</Medium></P>
						<P>Identity and Contact Data from publicly available sources such as Companies House and the Electoral Register based inside the United Kingdom.</P>
					</li>
				</ul>
			</div>
		)
	},
	{
		id: 'DataUse',
		title: 'How we use your personal data',
		content: (
			<div>
				<P>We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances:</P>
				<ul>
					<li><P>where we need to perform the contract we are about to enter into or have entered into with you;</P></li>
					<li><P>to verify identities and assist you if you forget your password or registered login details;</P></li>
					<li><P>to enable you to use and to assist you in using our website and the Plexi App;</P></li>
					<li><P>to enhance delivery of our website and the Plexi App;</P></li>
					<li><P>to communicate with you in relation to your use of our website and the Plexi App;</P></li>
					<li><P>for our marketing, promotional and publicity purposes, including to carry out direct marketing, market research and surveys (see the <a href="#Marketing">Marketing</a> and <a href="#Promotional">Promotional offers from us</a> headings below for more information);</P></li>
					<li><P>where it is necessary for our legitimate interests (or those of a third party) and your interests and fundamental rights do not override those interests;</P></li>
					<li><P>where we need to comply with a legal obligation; and/or</P></li>
					<li><P>any other use that you authorise.</P></li>
				</ul>
				<P><a href="#Glossary">Click here</a> to find out more about the types of lawful basis that we will rely on to process your personal data.</P>
				<P>Generally, we do not rely on consent as a legal basis for processing your personal data although we will get your consent before sending third party direct marketing communications to you via email or text message. You have the right to withdraw consent to marketing at any time by contacting us.</P>
				<P>Note that we may process your personal data for more than one lawful ground depending on the specific purpose for which we are using your data. Please contact us if you need details about the specific legal ground we are relying on to process your personal data.</P>


				<H6 id="Marketing">Marketing</H6>
				<P>We strive to provide you with choices regarding certain personal data uses, particularly around marketing and advertising.</P>

				<H6 id="Promotional">Promotional offers from us</H6>
				<P>We may use your Identity, Contact, Technical, Usage and Profile Data to form a view on what we think you may want or need, or what may be of interest to you. This is how we decide which products, services and offers may be relevant for you (we call this marketing).</P>
				<P>You will receive marketing communications from us if you have requested information from us or purchased services from us and you have not opted out of receiving that marketing and we may also carry out business to business marketing using publicly available contact details.</P>

				<H6>Third-party marketing</H6>
				<P>We will get your express opt-in consent before we share your personal data with any third party for marketing purposes.</P>

				<H6>Opting out</H6>
				<P>You can ask us or third parties to stop sending you marketing messages at any time by following the opt-out links on any marketing message sent to you or by contacting us at any time.</P>
				<P>Where you opt out of receiving these marketing messages, this will not apply to personal data provided to us as a result of a product/service purchase or other transactions.</P>

				<H6>Cookies</H6>
				<P>You can set your browser to refuse all or some browser cookies, or to alert you when websites set or access cookies. If you disable or refuse cookies, please note that some parts of our website may become inaccessible or not function properly. For more information about the cookies we use, please see our <Link to="/cookies/">Cookie Policy</Link>.</P>

				<H6>Change of purpose</H6>
				<P>We will only use your personal data for the purposes for which we collected it, unless we reasonably consider that we need to use it for another reason and that reason is compatible with the original purpose. If you wish to get an explanation as to how the processing for the new purpose is compatible with the original purpose, please contact us.</P>
				<P>If we need to use your personal data for an unrelated purpose, we will notify you and we will explain the legal basis which allows us to do so.</P>
				<P>Please note that we may process your personal data without your knowledge or consent, in compliance with the above rules, where this is required or permitted by law.</P>
			</div>
		)
	},
	{
		id: 'DataDisclosures',
		title: 'Disclosures of your personal data',
		content: (
			<div>
				<P>We may share your personal data with the parties set out below for the purposes set out above:</P>
				<ul>
					<li><P>Service providers acting as processors based in the United Kingdom, Ireland and the USA who provide IT and system administration services.</P></li>
					<li><P>Professional advisers acting as processors or joint controllers including lawyers, bankers, auditors and insurers based in the United Kingdom who provide consultancy, banking, legal, insurance and accounting services.</P></li>
					<li><P>HM Revenue & Customs, regulators and other authorities acting as processors or joint controllers based in the United Kingdom who require reporting of processing activities in certain circumstances.</P></li>
					<li><P>Third parties to whom we may choose to sell, transfer or merge parts of our business or our assets. Alternatively, we may seek to acquire other businesses or merge with them. If a change happens to our business, then the new owners may use your personal data in the same way as set out in this privacy policy.</P></li>
				</ul>
				<P>
					We require all third parties to respect the security of your personal data and to treat it in accordance with the law. We do not allow our third-party service providers to use your personal data for their own purposes and only permit them to process your personal data for specified purposes and in accordance with our instructions.
				</P>
			</div>
		)
	},
	{
		id: 'InternationalTransfers',
		title: 'International Transfers',
		content: (
			<div>
				<P>
					Whenever we transfer your personal data out of the United Kingdom, we ensure a similar degree of protection is afforded to it by ensuring at least one of the following safeguards is implemented:<br />
				</P>
				<ul>
					<li><P>We will only transfer your personal data to countries that have been deemed to provide an adequate level of protection for personal data by the United Kingdom authorities; or</P></li>
					<li><P>Where we use certain service providers, we may use specific contracts approved for use in the United Kingdom which give personal data the same protection it has in the United Kingdom.</P></li>
				</ul>
				<P>
					Please contact us if you want further information on the specific mechanism used by us when transferring your personal data out of the United Kingdom.
				</P>
			</div>
		)
	},
	{
		id: 'DataSecurity',
		title: 'Data Security',
		content: (
			<div>
				<P>We have put in place appropriate security measures to prevent your personal data from being accidentally lost, used or accessed in an unauthorised way, altered or disclosed. In addition, we limit access to your personal data to those employees, agents, contractors and other third parties who have a business need to know. They will only process your personal data on our instructions and they are subject to a duty of confidentiality.</P>
				<P>We have put in place procedures to deal with any suspected personal data breach and will notify you and any applicable regulator of a breach where we are legally required to do so.</P>
			</div>
		)
	},
	{
		id: 'DataRetention',
		title: 'Data Retention',
		content: (
			<div>
				<H6>How long will you use my personal data for?</H6>
				<P>We will only retain your personal data for as long as reasonably necessary to fulfil the purposes we collected it for, including for the purposes of satisfying any legal, regulatory, tax, accounting or reporting requirements. We may retain your personal data for a longer period in the event of a complaint or if we reasonably believe there is a prospect of litigation in respect to our relationship with you.</P>
				<P>To determine the appropriate retention period for personal data, we consider the amount, nature and sensitivity of the personal data, the potential risk of harm from unauthorised use or disclosure of your personal data, the purposes for which we process your personal data and whether we can achieve those purposes through other means, and the applicable legal, regulatory, tax, accounting or other requirements.</P>
				<P>By law we have to keep basic information about our customers (including Contact, Identity, Financial and Transaction Data) for six years after they cease being customers for tax purposes.</P>
				<P>In some circumstances you can ask us to delete your data: see <a href="#LegalRights">your legal rights</a> below for further information.</P>
				<P>In some circumstances we will anonymise your personal data (so that it can no longer be associated with you) for research or statistical purposes, in which case we may use this information indefinitely without further notice to you.</P>
			</div>
		)
	},
	{
		id: 'LegalRights',
		title: 'Your Legal Rights',
		content: (
			<div>
				<P>Under certain circumstances, you have rights under data protection laws in relation to your personal data. You have the right to:</P>
				<P>Request access to your personal data (commonly known as a "data subject access request"). This enables you to receive a copy of the personal data we hold about you and to check that we are lawfully processing it.</P>
				<P>Request correction of the personal data that we hold about you. This enables you to have any incomplete or inaccurate data we hold about you corrected, though we may need to verify the accuracy of the new data you provide to us.</P>
				<P>Request erasure of your personal data. This enables you to ask us to delete or remove personal data where there is no good reason for us continuing to process it. You also have the right to ask us to delete or remove your personal data where you have successfully exercised your right to object to processing (see below), where we may have processed your information unlawfully or where we are required to erase your personal data to comply with local law. Note, however, that we may not always be able to comply with your request of erasure for specific legal reasons which will be notified to you, if applicable, at the time of your request.</P>
				<P>Object to processing of your personal data where we are relying on a legitimate interest (or those of a third party) and there is something about your particular situation which makes you want to object to processing on this ground as you feel it impacts on your fundamental rights and freedoms. You also have the right to object where we are processing your personal data for direct marketing purposes. In some cases, we may demonstrate that we have compelling legitimate grounds to process your information which override your rights and freedoms.</P>
				<P>Request restriction of processing of your personal data. This enables you to ask us to suspend the processing of your personal data in the following scenarios:</P>
				<ul>
					<li><P>If you want us to establish the data's accuracy.</P></li>
					<li><P>Where our use of the data is unlawful but you do not want us to erase it.</P></li>
					<li><P>Where you need us to hold the data even if we no longer require it as you need it to establish, exercise or defend legal claims.</P></li>
					<li><P>You have objected to our use of your data but we need to verify whether we have overriding legitimate grounds to use it.</P></li>
				</ul>
				<P>Request the transfer of your personal data to you or to a third party. We will provide to you, or a third party you have chosen, your personal data in a structured, commonly used, machine-readable format. Note that this right only applies to automated information which you initially provided consent for us to use or where we used the information to perform a contract with you.</P>
				<P>Withdraw consent at any time where we are relying on consent to process your personal data. However, this will not affect the lawfulness of any processing carried out before you withdraw your consent. If you withdraw your consent, we may not be able to provide certain products or services to you. We will advise you if this is the case at the time you withdraw your consent.</P>
				<P>If you wish to exercise any of your legal rights, please contact us.</P>
				<H6>No fee usually required</H6>
				<P>You will not have to pay a fee to access your personal data (or to exercise any of the other rights). However, we may charge a reasonable fee if your request is clearly unfounded, repetitive or excessive. Alternatively, we could refuse to comply with your request in these circumstances.</P>
				<H6>What we may need from you</H6>
				<P>We may need to request specific information from you to help us confirm your identity and ensure your right to access your personal data (or to exercise any of your other rights). This is a security measure to ensure that personal data is not disclosed to any person who has no right to receive it. We may also contact you to ask you for further information in relation to your request to speed up our response.</P>
				<H6>Time limit to respond</H6>
				<P>We try to respond to all legitimate requests within one month. Occasionally it could take us longer than a month if your request is particularly complex or you have made a number of requests. In this case, we will notify you and keep you updated.</P>
			</div>
		)
	},
	{
		id: 'Glossary',
		title: 'Glossary',
		content: (
			<div>
				<H6>Lawful Basis</H6>
				<P><Medium>Legitimate Interest</Medium> means the interest of our business in conducting and managing our business to enable us to give you the best service/product and the best and most secure experience. We make sure we consider and balance any potential impact on you (both positive and negative) and your rights before we process your personal data for our legitimate interests. We do not use your personal data for activities where our interests are overridden by the impact on you (unless we have your consent or are otherwise required or permitted to by law). You can obtain further information about how we assess our legitimate interests against any potential impact on you in respect of specific activities by contacting us.</P>
				<P><Medium>Performance of Contract</Medium> means processing your data where it is necessary for the performance of a contract to which you are a party or to take steps at your request before entering into such a contract.</P>
				<P><Medium>Comply with a legal obligation</Medium> means processing your personal data where it is necessary for compliance with a legal obligation that we are subject to.</P>
			</div>
		)
	},

]

const contentWrapperStyles = css({
	margin: "0 auto",
	maxWidth: "640px",
	paddingTop: "120px",
	paddingLeft: "30px",
	paddingRight: "30px",
	minWidth: "300px",

	"& a:visited": {
		color: colours.purple,
	},
	"& ol": {
		color: colours.purple,
		fontSize: "14px",
		fontWeight: "600",
	},

	"& a": {
		color: colours.purple,
		cursor: "pointer",
	}
})

const Privacy = ({ location }) => {
	const renderIndex = () => {
		return (
			privacyPolicySections.map(({ id, title }, index) => (
				<li
					key={index}
					{...css({
						margin: "0 0 8px 0",
						lineHeight: "24px",
						fontWeight: 600,
						textTransform: "UPPERCASE",
					})}>
					<a href={`#${id}`}>
						{title}
					</a>
				</li>
			))
		)
	}

	const renderSections = () => {
		return privacyPolicySections.map(({ id, title, content }, index) => (
			<div
				id={id}
				key={index}
				{...css({
					"&::before": {
						display: "block",
						content: " ",
						marginTop: "-80px",
						height: "80px",
						visibility: "hidden",
						pointerEvents: "none",
					}
				})}
			>
				<H5>{title}</H5>
				{content}
			</div>
		))
	}

	return (
		<div>
			<Seo
				title={"Privacy Policy"}
				image={IndexSeo}
				location={location}
				meta={[{
					name: "robots",
					content: "noindex, nofollow"
				}]}
			/>
			<LegalHeader pageTitle={"Privacy Policy"} />
			<div {...contentWrapperStyles}>
				<H1>Intro</H1>
				<P>Plexi Software Limited is referred to as &quot;Plexi&quot;, &quot;we&quot;, &quot;us&quot; or &quot;our&quot; in this privacy policy.</P>
				<P>Welcome to our privacy policy.</P>
				<P>We respect your privacy and are committed to protecting your personal data. This privacy policy
					will inform you as to how we look after your personal data when you visit our website (regardless
					of where you visit it from), use the Plexi Web Application software (&quot;Plexi App&quot;) or contact us. It
					will also tell you about your privacy rights and how the law protects you.</P>
				<P>This privacy policy is provided in a layered format so you can click through to the specific areas set
					out below. Please also use the Glossary to understand the meaning of some of the terms used in
					this privacy policy.</P>
				<div {...css({ marginTop: "48px" })}>
					<ol {...css({ marginLeft: "18px", })}>
						{renderIndex()}
					</ol>
				</div>
				{renderSections()}
			</div>
		</div>
	)
}

export default Privacy



